import React from 'react'
import { Mail, Clock, BookOpen, ArrowLeft } from 'lucide-react'

interface RecipeSubmittedStepProps {
  email: string
  onStartOver: () => void
}

export default function RecipeSubmittedStep({ email, onStartOver }: RecipeSubmittedStepProps) {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        {/* Success icon */}
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <BookOpen className="w-10 h-10 text-emerald-600" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          Your cookbook is being created!
        </h1>

        <p className="text-gray-600 mb-6">
          We're matching your children's tastes, routines, and dietary needs with the best recipe options.
        </p>

        {/* Time estimate */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center gap-2 text-amber-800">
            <Clock className="w-5 h-5" />
            <span className="font-medium">This may take 5-10 minutes</span>
          </div>
        </div>

        {/* Email notification */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Mail className="w-5 h-5 text-emerald-600" />
            <span className="font-semibold text-emerald-800">Check your email</span>
          </div>
          <p className="text-emerald-700">
            Your personalised cookbook will be sent to:
          </p>
          <p className="text-emerald-900 font-medium mt-2 text-lg">
            {email}
          </p>
        </div>

        {/* What's included */}
        <div className="text-left mb-8">
          <h2 className="font-semibold text-gray-900 mb-3">Your cookbook will include:</h2>
          <ul className="space-y-2 text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-1">✓</span>
              <span>15+ personalised recipes based on your preferences</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-1">✓</span>
              <span>A weekly meal plan for easy planning</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-1">✓</span>
              <span>Quick emergency meals for busy days</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-1">✓</span>
              <span>Tips and guidance for your nanny</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-1">✓</span>
              <span>Allergy and safety information</span>
            </li>
          </ul>
        </div>

        {/* Start over button */}
        <button
          onClick={onStartOver}
          className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Create another cookbook
        </button>
      </div>

      {/* Didn't receive email notice */}
      <div className="mt-6 text-center text-sm text-gray-500">
        <p>
          Didn't receive the email? Check your spam folder, or{' '}
          <a href="mailto:rachel.carrell@korukids.co.uk" className="text-emerald-600 hover:underline">
            contact us
          </a>{' '}
          for help.
        </p>
      </div>
    </div>
  )
}
